import axios from "axios";

const API = axios.create({
  baseURL:
    process.env.REACT_APP_API_URL ||
    "https://localhost:7243/api",
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// =========================
// REQUEST INTERCEPTOR
// =========================
API.interceptors.request.use((config) => {
  return config;
});

// =========================
// RESPONSE INTERCEPTOR
// =========================
API.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      window.location.href = "/login";
    }

    return Promise.reject(error);
  }
);

// =========================
// AUTH API
// =========================
export const AuthAPI = {
  login: (email: string, password: string) =>
    API.post("/auth/login", {
      email,
      password,
    }),

  register: (data: any) =>
    API.post("/auth/register", data),

  forgotPassword: (email: string) =>
    API.post("/auth/forgot-password", {
      email,
    }),

  resetPassword: (token: string, newPassword: string) =>
    API.post("/auth/reset-password", {
      token,
      newPassword,
    }),
};

// =========================
// COMPANY API
// =========================
export const CompanyAPI = {
  registerCompany: (data: any) =>
    API.post("/RegisterCompany", data),

  getUsers: () =>
    API.get("/CompanyAdmin/company-users"),

  addUser: (data: any) =>
    API.post("/CompanyAdmin/add-user", data),

  removeUser: (data: any) =>
    API.post("/CompanyAdmin/remove-user", data),

  removeUserFromProject: (data: any) =>
    API.post("/CompanyAdmin/remove-user-from-project", data),

  assignUserToProject: (data: any) =>
    API.post("/CompanyAdmin/assign-user-to-project", data),

  getAvailableUsers: () =>
    API.get("/CompanyAdmin/available-users"),

  getUserRole: (email: string) =>
    API.get(
      `/CompanyAdmin/user-role?email=${encodeURIComponent(email)}`
    ),

  updateUserRole: (data: any) =>
    API.put("/CompanyAdmin/update-user-role", data),
};

// =========================
// PROJECT API
// =========================
export const ProjectAPI = {
  getAll: async () => {
    const res = await API.get("/Projects");
    return res.data;
  },

  getUsers: async (projectNo: number) => {
    const res = await API.get(
      `/projects/${projectNo}/users`
    );

    return res.data;
  },
};
// =========================
// TENDER API
// =========================
/* export const TenderAPI = {
  getAll: async () => {
    const res = await API.get("/Tenders");
    return res.data;
  },
}; */

// =========================
// TENDER API
// =========================
export const TenderAPI = {
  // Display / load tenders
  getAll: async () => {
    const res = await API.get("/Tenders");
    return res.data;
  },

  


  
  // Get contracts belonging to a project
  getByProject: async (projectNo: number) => {
    const res = await API.get(
      `/Tenders/project/${projectNo}`
    );
    return res.data;
  },

  
  // Get contractors associated with a selected project
  getContractorsByProject: async (projectNo: number) => {
    const res = await API.get(
      `/Tenders/project/${projectNo}/contractors`
    );
    return res.data;
  },

  
  // Get contractors where AccountHead = 11
  getContractors: async (): Promise<
    { vendorNo: number; vendor: string }[]
  > => {
    const res = await API.get("/Tenders/contractors");
    return res.data;
  },


  // Create new tender
  create: async (data: {
    tender: string;
    tenderDate: string | null;
    bidDate: string | null;
    tenderType: string | null;
    project: number | null;
    workNode: number | null;
    lastSession: number | null;
    changeType: string | null;
    contractDate: string | null;
    contractor: number | null;
  }) => {
    const res = await API.post("/Tenders", data);
    return res.data;
  },
};


export const SessionAPI = {
  create: async (
    projectNo: number,
    userNo: number | null
  ): Promise<{ sessionNo: number; sessionDate: string }> => {
    const response = await API.post("/Session/create", {
      projectNo,
      userNo,
    });

    return response.data;
  },
};
// =========================
// TENDER TREE API
// =========================

/* export type ProcedureNode = {
  workGroupNo: number;
  workGroup: string | null;

  procedureNo: number;
  procedure: string | null;

  
  treeTag: number;
}; */
export type ProcedureNode = {
  workGroupNo: number;
  workGroup: string | null;

  procedureNo: number;
  procedure: string | null;

  // Activity table
  // Procedure.Activity -> Activity.ActivityNo
  activityNo: number | null;

  // Activity.GeometryType
  geometryType: number | null;

  treeTag: number;
};


// =========================
// WORKGROUP TYPE
// =========================

export type WorkGroupNode = {
  workGroupNo: number;
  workGroup: string | null;
  workGroupDesc: string | null;

  projectNo: number | null;
  wbs: number | null;

  // Desktop convention:
  // WorkGroup = negative WorkGroupNo
  treeTag: number;

  // Procedures belonging to this WorkGroup
  procedures: ProcedureNode[];
};


// =========================
// WORKTREE TYPE
// =========================

export type WorkTreeNode = {
  treeNodeNo: number;
  workTreeName: string | null;
  parentNode: number | null;
  description: string | null;
  project: number | null;
  sequence: number | null;

  // Child WorkTree nodes
  children: WorkTreeNode[];

  // WorkGroups belonging to this WorkTree
  workGroups: WorkGroupNode[];
};


// =========================
// ADD SUB WORKS TREE TYPES
// =========================

export type AddSubWorksWorkGroupNode = {
  workGroupNo: number;
  workGroup: string | null;
  workGroupDesc: string | null;
  projectNo: number | null;
  wbs: number | null;
};

export type AddSubWorksWbsNode = {
  treeNodeNo: number;
  workTreeName: string | null;
  parentNode: number | null;
  description: string | null;
  project: number | null;
  sequence: number | null;
  children: AddSubWorksWbsNode[];
  workGroups: AddSubWorksWorkGroupNode[];
};

/* export type AddSubWorksTreeResponse = {
  success: boolean;
  tenderNo: number;
  projectNo: number;
  data: AddSubWorksWbsNode[];
}; */
export type AddSubWorksTreeResponse = {
  success: boolean;
  tenderNo: number;
  projectNo: number;
  wbsNo?: number | null;
  data: AddSubWorksWbsNode[];
  workGroups?: AddSubWorksWorkGroupNode[];
};

// =========================
// TENDER TREE RESPONSE
// =========================

// =========================
// ACTIVITY TYPE
// =========================

/* export type ActivityNode = {
  activityNo: number | null;
  procedureNo: number;
  activity: string | null;
  geometryType: number | null;
}; */

export type ActivityNode = {
  activityNo: number | null;
  activity: string | null;
  wbsNo?: number | null;
  geometryType?: number | null;
};
export type SubItemWbsNode = {
  treeNodeNo: number;
  workTreeName: string | null;
  parentNode: number | null;
  description?: string | null;
  project?: number | null;
  sequence?: number | null;
  children: SubItemWbsNode[];
  activities: ActivityNode[];
};

export type SubItemActivitiesResponse = {
  success?: boolean;
  rateGroupNo?: number;
  tenderNo?: number;
  projectNo?: number;
  wbsNo?: number | null;
  wbsTree?: SubItemWbsNode[];
  activities?: ActivityNode[];
};

// =========================
// WORKGROUP TREE RESPONSE TYPE
// =========================

export type WorkGroupTreeNode = {
  workGroupNo: number;
  workGroup: string | null;
  workGroupDesc: string | null;

  projectNo: number | null;
  wbs: number | null;

  transaction: string | null;
  billableWork: string | null;

  netCost: number | null;

  lastSession: number | null;
  changeType: string | null;

  subcontractor: string | null;
  subContractValue: number | null;

  client: string | null;
  clientValue: number | null;

  wStartDate: string | null;
  wEndDate: string | null;
  wDuration: number | null;

  wgHead: string | null;

  isDeleted: boolean;

  cBase: number | null;
};


export type UnitOption = {
  unitNo: number;
  unitName: string;
  geometryType: number;
  conversionFactor: number | null;
};
export type AvailableSpec = {
  id: number;
  itemId: number;
  name: string;
  type: "Activity" | "Component";
  component: number;
};

export type SpecRelatedItem = {
  id: number;
  name: string;
};

// =========================
// TENDER TREE RESPONSE
// =========================

export type TenderTreeResponse = {
  tenderNo: number;
  project: number | null;
  activities: ActivityNode[];
};


// =========================
// TENDER TREE API
// =========================

export const TenderTreeAPI = {

  // Load activities for the tender
  //
  // Tender
  //   ↓
  // Project
  //   ↓
  // Activities

  getByTender: async (
    tenderNo: number
  ): Promise<TenderTreeResponse> => {

    const res = await API.get(
      `/TenderTree/${tenderNo}`
    );

    return res.data;
  },

  getWorkGroups: async (
    tenderNo: number
  ): Promise<{
    success: boolean;
    tenderNo: number;
    projectNo: number | null;
    data: WorkGroupTreeNode[];
  }> => {

    const res = await API.get(
      `/TenderTree/work-groups/${tenderNo}`
    );

    return res.data;
  },


    // Load hierarchical WBS tree for Add Sub Works.
  // This is separate from the Stage Item WorkGroups API.

  /* getAddSubWorksTree: async (
    tenderNo: number
  ): Promise<AddSubWorksTreeResponse> => {
    const res = await API.get(
      `/TenderTree/add-sub-work-tree/${tenderNo}`
    );

    return res.data;
  }, */
  getAddSubWorksTree: async (
  tenderNo: number,
  rateGroupNo?: number,
  rateType?: "Trade Item" | "Stage Item"
): Promise<AddSubWorksTreeResponse> => {
  const res = await API.get(
    `/TenderTree/add-sub-work-tree/${tenderNo}`,
    {
      params: {
        rateGroupNo,
        rateType,
      },
    }
  );

  return res.data;
},
 /*  getSubItemActivities: async (rateGroupNo: number) => { 
    const response = await API.get(
      `/TenderTree/subitem-activities/${rateGroupNo}`
    );

    return response.data;
  }, */
  getSubItemActivities: async (
  rateGroupNo: number
): Promise<SubItemActivitiesResponse> => {
  const response = await API.get<SubItemActivitiesResponse>(
    `/TenderTree/subitem-activities/${rateGroupNo}`
  );

  return response.data;
},
  
getStageItemActivities: async (rateGroupNo: number) => {
  const response = await API.get(
    `/TenderTree/stage-item-activities/${rateGroupNo}`
  );

  return response.data;
},

 
/* getUnitsByActivity: async (
  activityNo: number
): Promise<UnitOption[]> => {
  const res = await API.get(
    `/TenderTree/units/${activityNo}`
  );

  return res.data;
}, */


getUnitsByActivity: async (
  activityNo: number
): Promise<UnitOption[]> => {
  const res = await API.get(
    `/TenderTree/units/${activityNo}`
  );

  const payload = res.data;

  // Backend returns a direct array.
  if (Array.isArray(payload)) {
    return payload as UnitOption[];
  }

  // Backend returns an object containing the array.
  if (Array.isArray(payload?.data)) {
    return payload.data as UnitOption[];
  }

  // No valid unit list returned.
  return [];
},


  

/* getSpecsByActivity: async (
  activityNo: number
): Promise<AvailableSpec[]> => {
  const res = await API.get(
    `/TenderTree/specs-by-activity/${activityNo}`
  );

  const payload = res.data;
  const rows = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
      ? payload.data
      : [];

  return rows.map((spec: any) => {
    const id = Number(
      spec.id ?? spec.componentGroupNo ?? 0
    );

    return {
      id,
      itemId: Math.abs(id),
      name: String(
        spec.name ?? spec.componentGroup ?? ""
      ),
      type: id < 0 ? "Activity" : "Component",
    };
  });
}, */

/* getSpecsByRateGroup: async (
  rateGroupNo: number
): Promise<AvailableSpec[]> => {
  const res = await API.get(
    `/TenderTree/specs-by-rate-group/${rateGroupNo}`
  );

  const payload = res.data;

  const rows = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
      ? payload.data
      : [];

  return rows.map((spec: any) => {
    const id = Number(
      spec.id ?? spec.componentGroupNo ?? 0
    );

    return {
      id,
      itemId: Math.abs(id),
      name: String(
        spec.name ?? spec.componentGroup ?? ""
      ),
      type: id < 0 ? "Activity" : "Component",
    };
  });
},
 */
getSpecsByRateGroup: async (
  rateGroupNo: number
): Promise<AvailableSpec[]> => {
  const res = await API.get(
    `/TenderTree/specs-by-rate-group/${rateGroupNo}`
  );

  const payload = res.data;

  const rows = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
      ? payload.data
      : [];

  return rows.map((spec: any) => {
    const id = Number(
      spec.id ?? spec.componentGroupNo ?? 0
    );

    const component = Number(
      spec.component ?? spec.Component ?? 0
    );

    return {
      id,
      itemId: Math.abs(id),
      name: String(
        spec.name ?? spec.componentGroup ?? ""
      ),
      type: component < 0 ? "Activity" : "Component",
      component,
    };
  });
},
getSpecRelatedItems: async (
  selectedId: number
): Promise<SpecRelatedItem[]> => {
  const res = await API.get(
    `/TenderTree/spec-related-items/${selectedId}`
  );

  const payload = res.data;
  const rows = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
      ? payload.data
      : [];

  return rows.map((item: any) => ({
    id: Number(item.id ?? item.procedureNo ?? item.componentNo ?? 0),
    name: String(
      item.name ?? item.procedure ?? item.component ?? ""
    ),
  }));
},

  // Save a selected specification to dbo.RateGroupSpecs
  addRateGroupSpec: async (data: {
    rateGroupNo: number;
    component: number;
    selectedItemID: number;
  }) => {
    const res = await API.post(
      "/TenderTree/add-rate-group-spec",
      data
    );

    return res.data;
  },

  // Load specifications already saved for a RateGroup
  getSavedRateGroupSpecs: async (
    rateGroupNo: number
  ): Promise<{
    rateGroupNo: number;
    component: number;
    selectedItemID: number;
  }[]> => {
    /* const res = await API.get(
      `/TenderTree/saved-rate-group-specs/${rateGroupNo}`
    ); */
    const res = await API.get(
  `/TenderTree/rate-group-specs/${rateGroupNo}`
);

    const payload = res.data;

    const rows = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.data)
        ? payload.data
        : [];

    return rows.map((item: any) => ({
      rateGroupNo: Number(
        item.rateGroupNo ?? item.RateGroupNo ?? 0
      ),
      component: Number(
        item.component ?? item.Component ?? 0
      ),
      selectedItemID: Number(
        item.selectedItemID ?? item.SelectedItemID ?? 0
      ),
    }));
  },



};

export const ThirdWBSAPI = {
  getTree: async (projectNo: number, level: number) => {
    const response = await API.get(
      `/ThirdWbs/${projectNo}/${level}`
    );
    return response.data;
  },

  getActivities: async (
    projectNo: number,
    wbsNo: number,
    level: number
  ) => {
    const response = await API.get(
      `/ThirdWbs/activity/${projectNo}/${wbsNo}/${level}`
    );
    return response.data;
  },
 


  
};


// =========================
// CONTRACT TREE API
// =========================

// -------------------------
// ADD SECTION REQUEST
// -------------------------
export type AddSectionRequest = {
  contractNode: string;
  sectionNo?: string | null;
  contract: number;

  // Root section
  // For now always 0
  parentNode: number;

  // Always 0
  workNode: number;
};


// -------------------------
// ADD SECTION RESPONSE
// -------------------------
export type AddSectionResponse = {
  success: boolean;
  message: string;

  data: {
    contractNodeNo: number;
    contractNode: string;
    sectionNo?: string | null;
    contract: number;
    parentNode: number;
    workNode: number;
    lastSession: number;
    changeType: string;
  };
};


// -------------------------
// CONTRACT TREE NODE
// -------------------------
export type ContractTreeNode = {
  contractNodeNo: number;
  contractNode: string;
   sectionNo?: string | null;
  contract: number;
  parentNode: number;
  workNode: number;
  lastSession: number;
  changeType: string;
};


// -------------------------
// CONTRACT TREE API
// -------------------------
export const ContractTreeAPI = {

  // ==========================================================
  // ADD SECTION
  // POST:
  // /api/ContractTree/add-section
  // ==========================================================

  addSection: async (
    data: AddSectionRequest
  ): Promise<AddSectionResponse> => {

    const res = await API.post(
      "/ContractTree/add-section",
      data
    );

    return res.data;
  },


  // ==========================================================
  // GET CONTRACT TREE
  // GET:
  // /api/ContractTree/{contract}
  //
  // Example:
  // /api/ContractTree/1
  // ==========================================================

  getByContract: async (
    contract: number
  ): Promise<ContractTreeNode[]> => {

    const res = await API.get(
      `/ContractTree/${contract}`
    );

    return res.data.data;
  },

    // GET MAXIMUM SECTION LEVEL
  getMaxLevel: async (
    contract: number
  ): Promise<number> => {
    const res = await API.get(
      `/ContractTree/${contract}/max-level`
    );

    return res.data.intendLevel;
  },
    // ==========================================================
  // DELETE CONTRACT SECTION
  // DELETE:
  // /api/ContractTree/delete-section/{contractNodeNo}
  // ==========================================================

  deleteSection: async (
    contractNodeNo: number
  ): Promise<{ success: boolean; message: string; affectedRows?: number }> => {

    const res = await API.delete(
      `/ContractTree/delete-section/${contractNodeNo}`
    );

    return res.data;
  },

};

// =========================================================
// RATE GROUP API
// =========================================================

/* export type RateGroupItemRequest = {
  rateGroup: string;
  contractNode: number;
  rider: number;
  stage: number;
  contract: number;
  unit: number | null;
  rateType: string;

  rate: number;
  contractQty: number;
  mark: number;
  percent: number;
}; */
export type RateGroupItemRequest = {
  rateGroup: string;
  contractNode: number;
  rider: number;

  // WBS ID associated with this activity
  wbsNo: number | null;
  stage: number;
  contract: number;
  unit: number | null;
  rateType: string;
};

export type ExistingRateGroupActivity = {

  rateGroupNo: number;
 rateGroup: string | null;
  rider: number;
  rate: number | null;
  contractNode: number;
   stage: number;
   contract: number | null;
  unit: number | null;
    unitName: string | null;
  rateType: string | null;
    wbsNo?: number | null;
};

export type RateGroupAddResponse = {
  success: boolean;
  message: string;
  addedCount: number;
  skippedCount: number;
  addedRiders?: number[];
  skippedRiders?: number[];
};

export type StageItemRequest = {
  tenderNo: number;
  workGroup: number;
   contractNode: number;
    sessionNo: number;
};

export type StageItemResponse = {
  success: boolean;
  message: string;
  rateGroupNo: number;
  tenderNo: number;
  workGroup: number;
  stage: number;
  rateGroup: string;
};
// =========================================================
// RATE ITEM / SUB ITEM API
// =========================================================

export type RateItemRequest = {
  rateGroup: number;
  procedure: number;
};

// =========================================================
// RATE WORK GROUP / SUB WORKS API
// =========================================================

export type RateWorkGroupRequest = {
  rateGroup: number;
  workGroup: number;
};


export type ExistingRateWorkGroup = {
  rateWorkGroupNo: number;
  rateGroup: number;
  procedure: number;
  workGroup: number;
  lastSession: number | null;
  changeType: string | null;
  rate: number;
  workGroupName: string | null;
  workGroupDescription: string | null;
};

export type ExistingRateItem = {
  rateItemNo: number;
  rateGroup: number;
  procedure: number;
  workGroup: number;

   activityName: string | null;
  workGroupName: string | null;
  lastSession: number | null;
  changeType: string | null;
};


export type RateItemAddResponse = {
  success: boolean;
  message: string;
  addedCount: number;
  skippedCount: number;
};

export type RateWorkGroupAddResponse = {
  success: boolean;
  message: string;
  addedCount: number;
  skippedCount: number;
};

export type SpecRow = {
  specNo: number;
  componentGroup: number;
  specificationNo: number;
  detailNo: number;
  specification: string | null;
  detail: string | null;
};

export type ComponentOption = {
  componentNo: number;
  component: string | null;
};

export const ContractSpecsAPI = {
  // =========================================================
  // GET SPECIFICATIONS FOR PROJECT
  //
  // GET:
  // /api/ContractSpecs/project/{projectId}
  //
  // Only projectId is required.
  // No scope.
  // No parentNo.
  // =========================================================

  getByProject: async (
    projectId: number
  ): Promise<SpecRow[]> => {
    const res = await API.get(
      `/ContractSpecs/project/${projectId}`
    );

    return res.data;
  },

  // =========================================================
  // GET DETAIL OPTIONS
  //
  // GET:
  // /api/ContractSpecs/details/{componentGroup}
  // =========================================================

  getDetailOptions: async (
    componentGroup: number
  ): Promise<ComponentOption[]> => {
    const res = await API.get(
      `/ContractSpecs/details/${componentGroup}`
    );

    return res.data;
  },
};

export const RateGroupAPI = {

  // =======================================================
  // GET ALREADY ADDED ACTIVITIES
  // =======================================================

  getAddedActivities: async (
    contract: number
  ): Promise<ExistingRateGroupActivity[]> => {

    const res = await API.get(
      `/TenderTree/added-activities/${contract}`
    );

    return res.data.data;
  },

  // =======================================================
  // ADD ACTIVITIES TO RATE GROUP
  // =======================================================

  addItems: async (
    items: RateGroupItemRequest[],
     sessionNo: number
  ): Promise<RateGroupAddResponse> => {

    const res = await API.post(
      "/TenderTree/add-items",
      {
          sessionNo,
        items,
      }
    );

    return res.data;
  },

   // =======================================================
  // ADD STAGE ITEM
  // =======================================================

  addStageItem: async (
    data: StageItemRequest
  ): Promise<StageItemResponse> => {
    const res = await API.post(
      "/TenderTree/add-stage-item",
      data
    );

    return res.data;
  },

  // UPDATE RATE GROUP NAME AND RATE
updateRateGroup: async (data: {
rateGroupNo: number;
rateGroup: string;
rate: number;
}) => {
const res = await API.put(
"/TenderTree/update-rate-group",
data
);

return res.data;

},

// UPDATE UNIT FOR A RATE GROUP
updateRateGroupUnit: async (data: {
  rateGroupNo: number;
  unit: number | null;
}) => {
  const res = await API.put(
    "/TenderTree/update-rate-group-unit",
    data
  );

  return res.data;
},
};


// =========================================================
// RATE ITEM / SUB ITEM API
// =========================================================

export const RateItemAPI = {

  // =======================================================
  // GET EXISTING SUB ITEMS
  //
  // GET:
  // /api/TenderTree/rate-items/{rateGroupNo}
  // =======================================================

  getByRateGroup: async (
    rateGroupNo: number
  ): Promise<ExistingRateItem[]> => {

    const res = await API.get(
      `/TenderTree/rate-items/${rateGroupNo}`
    );

    return res.data.data;
  },


  // =======================================================
  // ADD SUB ITEMS
  //
  // POST:
  // /api/TenderTree/add-rate-items
  // =======================================================

  addItems: async (
    items: RateItemRequest[],
    sessionNo: number
  ): Promise<RateItemAddResponse> => {

    const res = await API.post(
      "/TenderTree/add-rate-items",
      {
        sessionNo,
        items,
      }
    );

    return res.data;
  },

};

// =========================================================
// RATE WORK GROUP / SUB WORKS API
// =========================================================

export const RateWorkGroupAPI = {

  // =======================================================
  // GET EXISTING SUB WORKS
  //
  // GET:
  // /api/TenderTree/rate-workgroups/{rateGroupNo}
  // =======================================================

  getByRateGroup: async (
    rateGroupNo: number
  ): Promise<ExistingRateWorkGroup[]> => {

    const res = await API.get(
      `/TenderTree/rate-workgroups/${rateGroupNo}`
    );

    return res.data.data;
  },
   

  // =======================================================
  // ADD SUB WORKS
  //
  // POST:
  // /api/TenderTree/add-rate-workgroups
  // =======================================================

  addItems: async (
    items: RateWorkGroupRequest[],
    sessionNo: number
  ): Promise<RateWorkGroupAddResponse> => {

    const res = await API.post(
      "/TenderTree/add-rate-workgroups",
      {
        sessionNo,
        items,
      }
    );

    return res.data;
  },

};
// =========================================================
// RATE GROUP DELETE API
// =========================================================

export const RateGroupDeleteAPI = {


    // DELETE MAIN RATE GROUP
  deleteRateGroup: async (rateGroupNo: number) => {
    const res = await API.delete(
      `/TenderTree/delete-rate-group/${rateGroupNo}`
    );
    return res.data;
  },
  // DELETE SUB ITEM
  deleteRateItem: async (rateItemNo: number) => {
    const res = await API.delete(
      `/TenderTree/delete-rate-item/${rateItemNo}`
    );
    return res.data;
  },

  // DELETE SUB WORK
  deleteRateWorkGroup: async (rateWorkGroupNo: number) => {
    const res = await API.delete(
      `/TenderTree/delete-rate-workgroup/${rateWorkGroupNo}`
    );
    return res.data;
  },

  // DELETE SPECIFICATION
  deleteRateGroupSpec: async (
    rateGroupNo: number,
    component: number,
    selectedItemID: number
  ) => {
    const res = await API.delete(
      "/TenderTree/delete-rate-group-spec",
      {
        params: {
          rateGroupNo,
          component,
          selectedItemID
        }
      }
    );

    return res.data;
  }

};


 

export default API;